import axios, { type AxiosResponse, AxiosError } from "axios";
import { attachRateLimitRetry } from "./withRateLimitRetry";

export interface User {
  id: string;
  email: string;
  name: string | null;
  role: string;
  emailVerified: boolean;
  indianCdc: string;
  profileImage?: string;
  mobileNumber?: string;
  address?: string;
}

export interface UserProfile extends User {
  lastLoginAt: Date | null;
  lastActivityAt: Date | null;
  loginCount: number;
}

export interface LoginResult {
  success: boolean;
  message: string;
  user: User;
}

export interface SignupDto {
  email: string;
  password: string;
  indianCdc: string;
  name?: string;
}

export interface LoginDto {
  email: string;
  password: string;
}

export interface ResetPasswordDto {
  email: string;
}

export interface ResetPasswordConfirmDto {
  token: string;
  newPassword: string;
}

export interface ApiResponse<T = unknown> {
  success?: boolean;
  message: string;
  data?: T;
  userId?: string;
  user?: User;
  canLogin?: boolean;
}

export interface AuthStatusResponse {
  success: boolean;
  authenticated: boolean;
  user: {
    id: string;
    email: string;
    name: string | null;
    role: string;
  };
}

export interface LoginRecord {
  id: string;
  loginAt: Date;
  ipAddress: string;
  userAgent: string;
  success: boolean;
}

export interface AccountSecurityStatus {
  success: boolean;
  message: string;
  data: {
    userId: string;
    isLocked: boolean;
    failedAttempts: number;
    passwordLastChanged: Date | null;
    lastLoginAt: Date | null;
    recentLogins: LoginRecord[];
  };
}

export interface ApiError {
  message: string;
  statusCode: number;
  error?: string;
}

// ============================================================================
// AXIOS CONFIGURATION
// ============================================================================

const API_BASE_URL = import.meta.env.VITE_BACKEND_URL;

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true, // This ensures cookies are sent with requests
  headers: {
    "Content-Type": "application/json",
  },
});

// Response interceptor for error handling
// Registered first so it sees the raw AxiosError (with response.status
// and Retry-After) before the mapper below reshapes it.
attachRateLimitRetry(apiClient);

apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError) => {
    const status = error.response?.status || 0;

    if (status === 401) {
      localStorage.clear();
      sessionStorage.clear();
      // Avoid hard reload if we're already on the public login route.
      // This prevents the login page from refreshing and hiding error messages.
      if (typeof window !== "undefined" && window.location.pathname !== "/") {
        window.location.href = "/";
      }
      // optionally: return here to stop further processing
    }

    const errorData = error.response?.data as
      | { message?: string; error?: string }
      | undefined;

    const apiError: ApiError = {
      message: errorData?.message || error.message || "An error occurred",
      statusCode: status,
      error: errorData?.error,
    };

    return Promise.reject(apiError);
  }
);


// ============================================================================
// AUTHENTICATION API FUNCTIONS
// ============================================================================

export interface IndosDetailsResponse {
  success: boolean;
}

export async function getIndosDetails(
  indosNumber: string
): Promise<IndosDetailsResponse> {
  // Public auth route. The admin /indos/:number endpoint requires a login,
  // so calling it from signup always returned 401 and looked like a bad number.
  const response = await apiClient.get<IndosDetailsResponse>(
    `/api/auth/indos/${encodeURIComponent(indosNumber.trim())}`
  );
  return {
    success: response.data?.success === true,
  };
}

/**
 * Register a new user account
 */
export async function signup(signupData: SignupDto): Promise<ApiResponse> {
  const response = await apiClient.post<ApiResponse>(
    "/api/auth/signup",
    signupData
  );
  return response.data;
}

/**
 * Verify user email address
 */
export async function verifyEmail(
  token: string
): Promise<ApiResponse<{ canLogin: boolean }>> {
  const response = await apiClient.post<ApiResponse<{ canLogin: boolean }>>(
    "/api/auth/verify",
    { token }
  );
  return response.data;
}

/**
 * Resend email verification
 */
export async function resendVerificationEmail(
  email: string
): Promise<ApiResponse> {
  const response = await apiClient.post<ApiResponse>(
    "/api/auth/resend-verification",
    { email }
  );
  return response.data;
}

/**
 * Authenticate user login - Auth Store Version
 */
export async function authStoreLogin(loginData: {
  email: string;
  password: string;
}): Promise<{ user: User }> {
  const response = await apiClient.post("/api/auth/login", loginData);
  return response.data;
}

/**
 * Authenticate user login - Original Version
 */
export async function login(loginData: {
  email: string;
  password: string;
}): Promise<{ success: boolean; data: ApiResponse }> {
  const response = await apiClient.post(
    "/api/auth/login",
    { email: loginData.email, password: loginData.password },
    { withCredentials: true }
  );
  return {
    success: response.status === 200,
    data: response.data,
  };
}

/**
 * Logout current user - Auth Store Version
 */
export async function authStoreLogout(): Promise<void> {
  await apiClient.post("/api/auth/logout").finally(() => {
    localStorage.clear();
    sessionStorage.clear();
  });
}

/**
 * Logout current user - Original Version
 */
export async function logout(): Promise<ApiResponse> {
  localStorage.clear();
  sessionStorage.clear();
  const response = await apiClient.post<ApiResponse>("/api/auth/logout");
  return response.data;
}

/**
 * Request password reset - Auth Store Version
 */
export async function authStoreResetPassword(
  email: string
): Promise<{ success: boolean; message: string }> {
  const response = await apiClient.post("/api/auth/reset-password", { email });
  return response.data;
}

/**
 * Request password reset - Original Version
 */
export async function requestPasswordReset(
  email: string
): Promise<ApiResponse> {
  const response = await apiClient.post<ApiResponse>(
    "/api/auth/reset-password",
    { email }
  );
  return response.data;
}

/**
 * Confirm password reset - Auth Store Version
 */
export async function authStoreResetPasswordConfirm(
  token: string,
  newPassword: string
): Promise<{ success: boolean; message: string }> {
  const response = await apiClient.post("/api/auth/reset-password/confirm", {
    token,
    newPassword,
  });
  return response.data;
}

/**
 * Confirm password reset - Original Version
 */
export async function confirmPasswordReset(
  resetData: ResetPasswordConfirmDto
): Promise<ApiResponse> {
  const response = await apiClient.post<ApiResponse>(
    "/api/auth/reset-password/confirm",
    resetData
  );
  return response.data;
}

/**
 * Initial signup - CDC + Email + Name
 */
export async function authStoreSignup(
  indianCdc: string,
  email: string,
  password: string,
  name: string = ""
): Promise<{ success: boolean; message: string; userId?: string }> {
  const response = await apiClient.post("/api/auth/signup", {
    indianCdc,
    email,
    password,
    name,
  });
  return response.data;
}

/**
 * Setup password with token
 */
export async function authStoreVerifyEmail(token: string): Promise<{
  success: boolean;
  message: string;
}> {
  const response = await apiClient.post("/api/auth/verify", {
    token,
  });
  return response.data;
}

/**
 * Resend verification email - Auth Store Version
 */
export async function authStoreResendVerificationEmail(
  email: string
): Promise<{ success: boolean; message: string }> {
  const response = await apiClient.post("/api/auth/resend-verification", {
    email,
  });
  return response.data;
}

/**
 * Update user profile - Auth Store Version
 */
export async function authStoreUpdateProfile(
  profileData: Partial<{
    aadhaarNumber?: string;
    indianCdc?: string;
    currentShipName?: string;
    mobileNumber?: string;
    currentShipImo?: string;
    seafarerSid?: string;
    name?: string;
    email?: string;
    [key: string]: string | number | boolean | undefined;
  }>
): Promise<{ success: boolean; message: string; user: User }> {
  // Prepare the data to send to the API
  const requestData = {
    ...profileData,
    aadhar: profileData.aadhaarNumber,
    cdc: profileData.indianCdc,
    ship: profileData.currentShipName,
    imo: profileData.currentShipImo,
    sid: profileData.seafarerSid,
    mobile: profileData.mobileNumber,
  };

  // If name or email are being updated, include them in the request
  if (profileData.name) {
    requestData.name = profileData.name;
  }
  if (profileData.email) {
    requestData.email = profileData.email;
  }

  try {
    const response = await apiClient.patch<ApiResponse<{ user: User }>>(
      "/api/users/me",
      requestData
    );

    return {
      success: response.data.success || false,
      message: response.data.message,
      user: response.data.data?.user as User,
    };
  } catch (error) {
    console.error("Update profile error:", error);
    throw new Error(
      error instanceof Error ? error.message : "Failed to update profile"
    );
  }
}

/**
 * Get current user profile
 */
export async function getCurrentUser(): Promise<UserProfile> {
  const response = await apiClient.get<UserProfile>("/api/users/me");
  return response.data;
}

/**
 * Initialize authentication
 */
export async function authStoreInitializeAuth(): Promise<{
  isAuthenticated: boolean;
  user?: User;
}> {
  try {
    const response = await apiClient.get<{ user: User }>("/api/users/me");
    return {
      isAuthenticated: true,
      user: response.data.user,
    };
  } catch (error: unknown) {
    if (error instanceof Error) {
      console.error("Authentication check failed:", error.message);
    }
    return {
      isAuthenticated: false,
    };
  }
}

export async function getAccountSecurityStatus(): Promise<AccountSecurityStatus> {
  const response = await apiClient.get<AccountSecurityStatus>(
    "/api/auth/security/account-status"
  );
  return response.data;
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Check if user is authenticated
 */
export async function checkAuthStatus(): Promise<boolean> {
  try {
    await getCurrentUser();
    return true;
  } catch {
    return false;
  }
}

/**
 * Safe logout with local cleanup
 */
export async function safeLogout(redirectCallback?: () => void): Promise<void> {
  try {
    await logout();
  } catch (error) {
    console.warn(
      "Logout API call failed, but proceeding with local cleanup:",
      error
    );
  } finally {
    localStorage.removeItem("user");
    sessionStorage.clear();
    if (redirectCallback) {
      redirectCallback();
    }
  }
}
